import type { ComponentLockView, ComponentRuntimeLocked } from './component-lock';
import type { AccountAppearance } from './auth';
import type { WorkspaceGrant } from './workspace';
import type { CapabilityName, ComponentManifest } from './component';

export const COMPONENT_RUNTIME_TELEMETRY_BATCH_SIZE = 20;
export const COMPONENT_DATA_SUBSCRIPTION_BATCH_SIZE = 32;

export interface ComponentRuntimeTelemetryEvent {
  name: 'loaded' | 'runtime_error' | 'ctx_succeeded' | 'ctx_failed';
  method?: string;
  durationMs?: number;
  message?: string;
}

export interface ComponentRuntimeContextView {
  identity: { userId: string; locale: string; timezone: string };
  workspace: {
    id: string;
    name: string;
    locale: string;
    timezone: string;
    /** @deprecated 兼容字段；值来自当前账户主题，而非工作台。 */
    appearance: AccountAppearance;
  };
  instance: { id: string; pageId: string; componentId: string; props: Record<string, unknown> };
  agent: {
    id: string | null;
    /** 只声明受控图片传递协议可用；具体模型能否看图由调用时校验。 */
    imageInput?: boolean;
    name?: string | null;
    avatar?: string | null;
    defaultEntryEnabled?: boolean;
  };
  settings: Record<string, unknown>;
  grants: CapabilityName[];
  /** 当前成员实际可执行的组件业务操作 key。 */
  operations: string[];
  unavailableCapabilities: CapabilityName[];
  missingWorkspaceGrants: WorkspaceGrant[];
}

export interface ComponentRuntimeDescriptor {
  licenseExpiresAt?: string | null;
  token: string;
  lock?: ComponentLockView;
  source: string;
  artifactHash?: string;
  tagName: string;
  context: ComponentRuntimeContextView;
  contract: Pick<ComponentManifest, 'actions' | 'events' | 'functions' | 'viewState'> & {
    viewStateVersionId?: string;
  };
}

/** 正式运行态在强制升级时不下发源码或 Capability Token。 */
export interface ComponentRuntimeUpgradeRequired {
  status: 'upgrade_required';
  componentName: string;
  currentVersion: string;
  minimumVersion: string;
  targetVersionId: string;
  requiredCapabilities: CapabilityName[];
  installationNotes: string[];
  canUpgrade: boolean;
}

export interface ComponentRuntimeLicenseRequired {
  status: 'license_required';
  packageId: string;
  releaseId: string;
  componentName: string;
  currentVersion: string;
  canPurchase: boolean;
}

export type ComponentRuntimeLoadResult =
  | ComponentRuntimeDescriptor
  | ComponentRuntimeUpgradeRequired
  | ComponentRuntimeLicenseRequired
  | ComponentRuntimeLocked;

/** 客户端已校验本地构建代码时，服务端只返回哈希与最新运行上下文。 */
export type ComponentRuntimeLoadResponse =
  | (Omit<ComponentRuntimeDescriptor, 'source'> & { source?: string })
  | ComponentRuntimeUpgradeRequired
  | ComponentRuntimeLicenseRequired
  | ComponentRuntimeLocked;

export interface ComponentPreviewSource {
  sourceType: 'local' | 'publication';
  componentId?: string;
  libraryEntryId?: string;
  versionId: string;
}

export interface ComponentPreviewSessionView {
  id: string;
  workspaceId: string;
  componentId: string;
  settings: Record<string, unknown>;
  generation: number;
  expiresAt: string;
}
